'use client';

import Link from 'next/link';
import { useActsLanguage } from '../../ustawa/utils/useActsLanguage';
import {
  ACTS_COPY,
  CHANGES_HUB_COPY,
  CHANGES_PAGE_COPY,
  formatTemplate,
} from '../../ustawa/utils/actsTranslations';
import type { CalendarDay } from '@/api/services/actChanges.service';
import PublicHeaderBrand from '../../components/PublicHeaderBrand';

/**
 * /zmiany-w-prawie/{year}/{month} - one month's change-sets grouped by date.
 * Each changed act links to its dated diff page (/ustawa/{slug}/zmiany/{date}),
 * where the reader sees the diffs and can watch specific articles. Months before
 * the versioning baseline (2026-07) render an honest "detection starts here" note.
 */
const BASELINE_YEAR = 2026;
const BASELINE_MONTH = 7;

export default function ZmianyMonthClient({
  year,
  month,
  days,
}: {
  year: number;
  month: number;
  days: CalendarDay[];
}) {
  const language = useActsLanguage();
  const hub = CHANGES_HUB_COPY[language];
  const changes = CHANGES_PAGE_COPY[language];
  const acts = ACTS_COPY[language];

  const monthName = (y: number, m: number): string => {
    try {
      return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString(
        language === 'uk' ? 'uk-UA' : 'pl-PL',
        { month: 'long', year: 'numeric', timeZone: 'UTC' },
      );
    } catch {
      return `${m}/${y}`;
    }
  };
  const formatDate = (iso: string): string => {
    try {
      return new Date(iso + 'T00:00:00Z').toLocaleDateString(
        language === 'uk' ? 'uk-UA' : 'pl-PL',
        { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' },
      );
    } catch {
      return iso;
    }
  };

  // Prev/next month, guarded so next never points into the future.
  const prev = month === 1 ? { y: year - 1, m: 12 } : { y: year, m: month - 1 };
  const next = month === 12 ? { y: year + 1, m: 1 } : { y: year, m: month + 1 };
  const now = new Date();
  const nextInFuture =
    next.y > now.getUTCFullYear() ||
    (next.y === now.getUTCFullYear() && next.m > now.getUTCMonth() + 1);
  const prevBeforeBaseline =
    prev.y < BASELINE_YEAR || (prev.y === BASELINE_YEAR && prev.m < BASELINE_MONTH);
  const isPreBaseline =
    year < BASELINE_YEAR || (year === BASELINE_YEAR && month < BASELINE_MONTH);

  const monthPath = (y: number, m: number) =>
    `/zmiany-w-prawie/${y}/${String(m).padStart(2, '0')}`;

  return (
    <main className="min-h-screen bg-[var(--surface-primary)]">
      <header className="bg-[var(--navbar-background)] text-white py-6">
        <div className="max-w-4xl mx-auto px-4">
          <PublicHeaderBrand />
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-10">
        <nav className="text-sm text-[var(--text-secondary)] mb-6 flex flex-wrap gap-2">
          <Link href="/" className="hover:text-[var(--interactive-primary)]">
            {acts.common.home}
          </Link>
          <span>/</span>
          <Link href="/zmiany-w-prawie" className="hover:text-[var(--interactive-primary)]">
            {hub.hubHeading}
          </Link>
          <span>/</span>
          <span className="text-[var(--text-primary)] capitalize">{monthName(year, month)}</span>
        </nav>

        <h1 className="text-3xl font-bold text-[var(--text-primary)] mb-6 capitalize">
          {formatTemplate(hub.monthHeading, { month: monthName(year, month).replace(/\s*\d{4}$/, ''), year })}
        </h1>

        <div className="flex justify-between mb-8 text-sm">
          {prevBeforeBaseline ? (
            <span className="text-[var(--text-secondary)] opacity-50">← {hub.prevMonth}</span>
          ) : (
            <Link
              href={monthPath(prev.y, prev.m)}
              className="hover:underline"
              style={{ color: 'var(--interactive-primary)' }}
            >
              ← {hub.prevMonth}
            </Link>
          )}
          {nextInFuture ? (
            <span className="text-[var(--text-secondary)] opacity-50">{hub.nextMonth} →</span>
          ) : (
            <Link
              href={monthPath(next.y, next.m)}
              className="hover:underline"
              style={{ color: 'var(--interactive-primary)' }}
            >
              {hub.nextMonth} →
            </Link>
          )}
        </div>

        {isPreBaseline && (
          <p
            className="rounded-lg p-4 mb-6 text-sm text-[var(--text-secondary)]"
            style={{ backgroundColor: 'var(--surface-secondary)' }}
          >
            {hub.preBaselineNote}
          </p>
        )}

        {days.length === 0 ? (
          <section
            className="rounded-lg p-8 text-center"
            style={{ backgroundColor: 'var(--surface-elevated)' }}
          >
            <h2 className="text-xl font-semibold text-[var(--text-primary)] mb-2">
              {hub.monthEmptyTitle}
            </h2>
            <p className="text-[var(--text-secondary)]">{hub.monthEmptyText}</p>
          </section>
        ) : (
          <ol className="list-none p-0 m-0 flex flex-col gap-5">
            {days.map((day) => (
              <li key={day.date}>
                <time
                  dateTime={day.date}
                  className="block text-lg font-semibold text-[var(--text-primary)] mb-2"
                >
                  {formatDate(day.date)}
                </time>
                <ul className="list-none p-0 m-0 flex flex-col gap-2">
                  {day.acts.map((act) => (
                    <li
                      key={act.slug}
                      className="rounded-lg p-4 flex flex-wrap items-center justify-between gap-3"
                      style={{ backgroundColor: 'var(--surface-elevated)' }}
                    >
                      <Link
                        href={`/ustawa/${act.slug}/zmiany/${day.date}`}
                        className="font-medium text-[var(--text-primary)] hover:underline break-words min-w-0"
                      >
                        {act.title}
                      </Link>
                      <span className="text-sm text-[var(--text-secondary)]">
                        {act.changed_articles_count === 1
                          ? changes.articlesChangedOne
                          : formatTemplate(changes.articlesChangedMany, {
                              count: act.changed_articles_count,
                            })}
                      </span>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ol>
        )}

        <Link
          href="/zmiany-w-prawie"
          className="inline-block mt-8 text-sm font-medium hover:underline"
          style={{ color: 'var(--interactive-primary)' }}
        >
          ← {hub.backToHub}
        </Link>
      </div>
    </main>
  );
}
